import { ENDPOINT_NAMES } from '../../../constants';
import type { ProjectSurvey } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PatchSurveyStatusArgs {
  projectId: number;
  surveyId: number;
  setSurveyStatusRequest: { surveyStatus?: string | null };
}

interface PatchSurveyStatusContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: ProjectSurvey }>;
  getState: () => RootState;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SurveyConfigArgs = {
  projectId: number;
  surveyType?: string;
};

/**
 * ### updateSurveyStatusInCache
 *
 * @description
 * Updates the survey status for a specific survey in a draft array.
 * This is a survey-specific helper that works with both Survey and SurveyConfig types.
 *
 * @example
 * updateSurveyStatusInCache(draft, 123, 'ready');
 *
 * @param draft - The draft array containing surveys or survey configs
 * @param surveyId - The ID of the survey to update
 * @param newStatus - The new status to set (can be null)
 */
const updateSurveyStatusInCache = <
  T extends { surveyId?: number | null; surveyStatus?: string | null },
>(
  draft: T[] | undefined,
  surveyId: number,
  newStatus: string | null,
): void => {
  const item = draft?.find((s) => s.surveyId === surveyId);
  if (item) {
    item.surveyStatus = newStatus;
  }
};

/**
 * ### Patch Survey Status Enhancer
 *
 * @description
 * Enhancer for patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus endpoint.
 * Optimistically updates the survey status in the cache before the server responds.
 * If the request fails, the update is rolled back.
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus = (api: ProjectApi) => ({
  async onQueryStarted(args: PatchSurveyStatusArgs, context: PatchSurveyStatusContext) {
    const { projectId, surveyId, setSurveyStatusRequest } = args;
    const { dispatch, queryFulfilled } = context;
    const newStatus = setSurveyStatusRequest.surveyStatus;

    if (newStatus === undefined) {
      return;
    }

    const updater = new OptimisticUpdater({ dispatch, api });

    // Optimistically update the surveys list cache
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEYS,
      args: { projectId },
      updateFn: (draft: ProjectSurvey[]) => updateSurveyStatusInCache(draft, surveyId, newStatus),
    });

    // Optimistically update ALL cached survey configs queries for this project
    // This handles queries with different surveyType parameters (e.g., SURVEY_TYPES.PRODUCT)
    const state = context.getState();
    const queries = state.projectApi?.queries;
    const matchingQueries = findMatchingCachedQueries<SurveyConfigArgs>({
      queries,
      endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS,
      matcher: (args) => args.projectId === projectId,
    });

    matchingQueries.forEach((args) => {
      updater.update({
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS,
        args,
        updateFn: (draft: ProjectSurvey[]) => updateSurveyStatusInCache(draft, surveyId, newStatus),
      });
    });

    try {
      await queryFulfilled;
    } catch {
      // Rollback all optimistic updates on failure
      updater.rollback();
    }
  },
});

export default patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus;
